import { type CSSProperties, forwardRef } from 'react';

import { skeletonSvgCSS } from './Skeleton.sty.js';
import { SkeletonDrawer, SkeletonGradient } from './utils.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useId } from '../../core/hooks/useId.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';

const COMPONENT_NAME = 'SkeletonText';

/**
 * Accepted properties for SkeletonText
 * @public
 */
export interface SkeletonTextProps
  extends StylingProps,
    DataTestId,
    AriaLabelingProps,
    BehaviorTrackingProps {
  /**
   * Width of the SkeletonText placeholder.
   * @defaultValue 100%
   */
  width?: CSSProperties['width'];
  /**
   * Number of Lines of the SkeletonText placeholder.
   * @defaultValue 3
   */
  lines?: number;
}

const lineHeight = 12;
const linePadding = 8;

const baseYOffset = linePadding * 0.5;
const lineCapSize = lineHeight * 0.5;
const lineStep = lineHeight + linePadding;

const getSkeletonLinesMargin = (line: number, lines: number) => {
  if (lines === 1) {
    // do not add any margin for single-line SkeletonTexts
    return 0;
  }
  return ((line + 2) % 3) * lineHeight;
};

/**
 * The `SkeletonText` component renders a specified number of placeholder boxes for lines of text with an animated background.
 * @public
 */
export const SkeletonText = forwardRef<HTMLDivElement, SkeletonTextProps>(
  (props, forwardedRef) => {
    const {
      width = '100%',
      lines = 3,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);
    const ariaLabelingProps = useAriaLabelingProps(restProps);
    const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);

    const maskId = useId();
    const gradientId = useId();

    return (
      <div
        ref={forwardedRef}
        className={consumerClassName}
        style={{
          width,
          height: `${lineStep * lines}px`,
          ...consumerStyle,
        }}
        data-testid={dataTestId}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <svg className={skeletonSvgCSS}>
          <defs>
            <SkeletonGradient id={gradientId} />
          </defs>
          <mask id={maskId}>
            {new Array(lines).fill(null).map((_, lineIndex) => (
              <rect
                key={`${lines}-${lineIndex}`}
                y={baseYOffset + lineStep * lineIndex}
                height={lineHeight}
                rx={lineCapSize}
                fill="white"
                style={{
                  // we also substract 1px, to make sure the line cap is fully within the svg, and isn't cut
                  width: `calc(100% - 1px - ${getSkeletonLinesMargin(
                    lineIndex,
                    lines,
                  )}px)`,
                }}
              />
            ))}
          </mask>
          <g mask={`url(#${maskId})`}>
            <SkeletonDrawer gradientId={gradientId} />
          </g>
        </svg>
      </div>
    );
  },
);

(SkeletonText as typeof SkeletonText & { displayName: string }).displayName =
  COMPONENT_NAME;
